import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Table, Tag, Typography, Input, Button, Avatar, Modal, Spin, Collapse, DatePicker, Popover } from 'antd';
import type { Dayjs } from 'dayjs';
import { SearchOutlined, HistoryOutlined, EyeOutlined, ArrowRightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { RobotOutlined, UserOutlined, LoadingOutlined, DeleteOutlined, MinusOutlined, ReloadOutlined } from '@ant-design/icons';
import axios, { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import {
  useSaveLibraryChanges,
  useCensoHistory,
  useCensoActivityHistory,
  useLibraryHistory,
} from '../hooks';
import { RATE_ENGINE_QUERY_KEYS, rateEngineApi } from '../api';
import type {
  CensoActivityChatResponse,
  CensoActivityCompletedOperation,
  CensoActivityPendingOperation,
  CensoChange,
  LibraryHistoryRecord,
  MutationOperationPlace,
  MutationOperationType,
  MutationPreviewResponse,
  RegionalLibraryItem,
  ResourceGroup,
} from '../types';

/**
 * Which conversation the Rate Assistant panel is running — chosen up front
 * from the action-choice prompt shown when the assistant is opened:
 *   • 'modify' (default) — the existing rate-adjustment conversation, unchanged.
 *   • 'addNew' — describing a brand new activity to add. There's no existing
 *     rate-library snapshot to connect with, so this mode skips the
 *     connect-then-chat handshake entirely and sends every turn via
 *     `rateEngineApi.censoAddActivity` (`{ session_id, projectId, message }`).
 */
export type CensoAssistantMode = 'modify' | 'addNew';

const { Text } = Typography;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const parseUtc = (s: string): Date =>
  new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);

const RESOURCE_GROUP_COLOURS: Record<ResourceGroup, string> = {
  Labour: 'blue',
  Plant: 'orange',
  Material: 'green',
  Subcontract: 'purple',
  Other: 'default',
};

// ─── Chat types ───────────────────────────────────────────────────────────────

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  retryConnect?: boolean;
  /** Activity Assistant only ("addNew" mode) — set when this reply is asking the
   * user to confirm a write. Rendered as Accept/Discard quick-reply buttons on the
   * most recent assistant message only; there is no separate confirm endpoint —
   * the user's answer is just sent as the next chat message. */
  pendingOperations?: CensoActivityPendingOperation[];
  /** Activity Assistant only — true while the pending operation's cost-impact
   * preview (`rateEngineApi.previewActivityMutation`) is in flight. The
   * preview itself renders in the main tree view's diff mode, not here — this
   * just gives the user a "still working on it" cue in the chat meanwhile. */
  mutationPreviewLoading?: boolean;
}

// Reuse the same Censo session across ChatPanel remounts for a given region/site
// (SPA route navigation between activity-tree/rate-manager, or the region/site
// key-remount) instead of minting a fresh one every time — the backend's
// disconnect endpoint isn't reliable, so we avoid needing it at all by never
// abandoning a session in the first place. Cleared on hard reload (module-scoped,
// not persisted storage), which matches a session's natural lifetime.
const censoSessionCache = new Map<string, string>();

const censoSessionCacheKey = (region: string, siteName: string | undefined, mode: CensoAssistantMode) =>
  `${region}::${siteName ?? ''}::${mode}`;

const WELCOME: ChatMessage = {
  id: 0,
  role: 'assistant',
  text: "Hi! I'm your AI Rate Assistant, powered by Censo.\n\nOnce connected, you can ask me to adjust rates by Code, Name, or Group. Here are some examples:\n\nBy Group:\n• \"Increase all Labour rates by 10%\"\n• \"Reduce Material by 5%\"\n• \"Raise Plant items by 15%\"\n\nBy Code:\n• \"Increase L001A by 20%\"\n• \"Reduce AGG0004 by 3%\"\n• \"Update SCBB001 to £6.50\"\n\nBy Name:\n• \"Increase 'Engineer' by 8%\"\n• \"Reduce 'Gravel' by 5%\"\n• \"Set 'Generator fuel - 200' to £2.00\"\n\nPro tip: You can combine multiple changes in one request, like \"Increase all Labour by 10% and reduce Diesel by 5%\"",
  timestamp: new Date(),
};

const WELCOME_ADD_NEW: ChatMessage = {
  id: 0,
  role: 'assistant',
  text: "Hi! I'm your Activity Assistant, powered by Censo.\nI can search this project's live activities and make changes to them — for example:\n\n• **Add a bill line:** Add a bill line called \"Test BL\" under section \"Ancilliary items\", Unit m2, Quantity 5\n• **Add an activity:** Add activity \"Test activity\" with code \"A001\" and quantity 5 under \"Excavate trench and tip on site\"\n• **Add a sub-activity:** Add sub-activity \"Sub activity\" with code \"SA001\" and quantity 1 under \"Excavate trench for founds and tip on site\"\n• **Delete an activity:** Delete activity \"Sub activity\" from \"Excavate trench for founds and tip on site\"\n• **Add a new item:** Add item \"Test item\" with code \"TESTITEM001\", quantity 1, rate 10.10, unit m, and resource plant under \"Test activity\"\n• **Add an existing item:** Add L001B to \"Test activity\" with quantity 10\n• **Set a multiplier:** Set multiplier A of L001B in \"Test activity\" to 2\n• **Set division:** Set divideBy of L001B in \"Test activity\" to 3\n• **Copy an item:** Copy GWALPF001 from \"Jetfloor - 150 beam, 75mm overlay 0.15w/m2k - TW\" to \"Test BL\"\n\nIf a name matches more than one place, I'll list the options for you to pick from.\nBefore I change anything, I'll ask you to confirm — just click \"Accept\" or \"Discard\".",
  timestamp: new Date(),
};

/**
 * Activity Assistant replies are plain text, not markdown/HTML — rendered
 * as-is with line breaks preserved (never run through a markdown renderer;
 * build-up listings use characters that would come out mangled). Lines may
 * start with a short tag that's a tone hint worth styling, not an enum to
 * branch logic on: `[?]` asking something, `[OK]` a change went through,
 * `[!]` refused/failed/cancelled, no prefix a plain/greeting line. The tag
 * itself is a wire-format hint, not something the user needs to read, so it's
 * stripped from the rendered line — the tone still comes through via colour
 * (per line) and, on the live chat bubble, a matching accent/tint on the
 * message box as a whole (`detectReplyTone` below).
 */
type ActivityReplyTone = 'question' | 'ok' | 'error';

const ACTIVITY_REPLY_TONE_STYLES: ReadonlyArray<{ prefix: string; tone: ActivityReplyTone; textColour: string; accent: string; tint: string }> = [
  { prefix: '[?]', tone: 'question', textColour: '#1677ff', accent: '#1677ff', tint: '#f0f7ff' },
  { prefix: '[OK]', tone: 'ok', textColour: '#389e0d', accent: '#389e0d', tint: '#f6ffed' },
  { prefix: '[!]', tone: 'error', textColour: '#ff4d4f', accent: '#ff4d4f', tint: '#fff1f0' },
];

/** The reply's overall tone, from the first tagged line found — drives the
 * live chat bubble's accent border/tint. `undefined` for a plain/greeting
 * reply with no tag at all. */
function detectReplyTone(text: string): ActivityReplyTone | undefined {
  for (const line of text.split('\n')) {
    const match = ACTIVITY_REPLY_TONE_STYLES.find(({ prefix }) => line.startsWith(prefix));
    if (match) return match.tone;
  }
  return undefined;
}

/** One tagged/plain reply line. A `[!]` line inside an otherwise successful
 * (`[OK]`) reply is a side note about a change that *did* go through (e.g. "the
 * activity is also used elsewhere, only this one was changed"), not a
 * failure — shown as an informational callout instead of red error text. */
function renderReplyLine(line: string, key: number, isLast: boolean, overallTone: ActivityReplyTone | undefined): React.ReactNode {
  const match = ACTIVITY_REPLY_TONE_STYLES.find(({ prefix }) => line.startsWith(prefix));
  const displayLine = match ? line.slice(match.prefix.length).trimStart() : line;
  if (match?.tone === 'error' && overallTone === 'ok') {
    return (
      <div
        key={key}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 8, margin: '6px 0',
          padding: '6px 10px', borderRadius: 6,
          background: '#e6f4ff', border: '1px solid #91caff', color: '#0958d9',
        }}
      >
        <InfoCircleOutlined style={{ marginTop: 3, flexShrink: 0 }} />
        <span>{displayLine}</span>
      </div>
    );
  }
  return (
    <React.Fragment key={key}>
      <span style={match ? { color: match.textColour } : undefined}>{displayLine}</span>
      {!isLast && <br />}
    </React.Fragment>
  );
}

function renderActivityReplyLines(text: string): React.ReactNode {
  const lines = text.split('\n');
  const overallTone = detectReplyTone(text);
  return lines.map((line, i) => renderReplyLine(line, i, i === lines.length - 1, overallTone));
}

// A disambiguation reply ("I found N matching places for X. Which one do you
// mean?") sends its options as plain numbered lines — `N. <location path>` —
// same wire format as any other reply, not a separate structured field.
// Detected structurally (2+ consecutive lines matching this shape) rather
// than off the intro wording, since that phrasing isn't a stable contract.
const DISAMBIGUATION_OPTION_RE = /^(\d+)\.\s+(.+)$/;

/** One clickable disambiguation option — a breadcrumb-style card (numbered
 *  badge, `>`-separated location path with the final/matched segment
 *  emphasised, a trailing arrow) rather than a plain labelled button, so a
 *  reply with several near-identical paths (same name, different code) stays
 *  scannable. Hover state is local component state, not inline
 *  onMouseEnter/onMouseLeave mutation, since it drives several dependent
 *  style values (border, background, shadow, arrow) at once. */
const DisambiguationOption: React.FC<{
  number: string;
  label: string;
  disabled: boolean;
  onSelect: () => void;
}> = ({ number, label, disabled, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const active = hovered && !disabled;
  const segments = label.split('>').map((s) => s.trim()).filter(Boolean);
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        width: '100%',
        textAlign: 'left',
        padding: '10px 12px',
        borderRadius: 10,
        border: `1px solid ${active ? '#8fbcff' : '#e6ecf5'}`,
        background: disabled ? '#f7f8fa' : (active ? '#f0f7ff' : '#fff'),
        boxShadow: active ? '0 3px 10px rgba(24,144,255,0.16)' : '0 1px 2px rgba(16,24,40,0.05)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transform: active ? 'translateY(-1px)' : 'none',
        transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
      }}
    >
      <span style={{
        flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: '#fff',
        background: active ? '#1677ff' : '#0B2545',
        transition: 'background 0.15s ease',
        marginTop: 1,
      }}>
        {number}
      </span>
      {/* Parent → child hierarchy, one level per line with a tree connector
          and increasing indent — not a horizontal breadcrumb — since a path
          this deep (routinely 4-5 levels) wrapped into an unreadable ragged
          paragraph as inline text. The matched leaf/item (last segment) is
          the only line that reads as "selected", everything above it is
          progressively lighter context. */}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {segments.map((seg, i) => {
          const isLast = i === segments.length - 1;
          return (
            <span
              key={i}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 5,
                // Capped, not just indented — an unbounded `paddingLeft` on a
                // 4-5 level path can itself push a deep segment's remaining
                // width down to nothing (or negative), the other contributor
                // to this card overflowing horizontally alongside the
                // `nowrap` text below.
                paddingLeft: Math.min(i * 12, 36),
                minWidth: 0,
                fontSize: isLast ? 12.5 : 11,
                lineHeight: isLast ? 1.5 : 1.7,
                color: isLast ? '#0B2545' : '#98a2b3',
                fontWeight: isLast ? 700 : 400,
              }}
            >
              {i > 0 && <span style={{ color: '#cdd5e0', fontSize: 11, flexShrink: 0 }}>└</span>}
              {/* Wraps instead of truncating — `minWidth: 0` is what makes a
                  flex child actually shrink/wrap to the row's width instead
                  of forcing it wider; `nowrap` + `ellipsis` here previously
                  had no bounded width to truncate against, so it just grew
                  the whole card (and the chat panel with it) instead. */}
              <span style={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{seg}</span>
            </span>
          );
        })}
      </span>
      <ArrowRightOutlined style={{
        flexShrink: 0, fontSize: 11, marginTop: 3,
        color: active ? '#1677ff' : '#c3cbd9',
        transform: active ? 'translateX(2px)' : 'none',
        transition: 'transform 0.15s ease, color 0.15s ease',
      }} />
    </button>
  );
};

/** Same rendering as `renderActivityReplyLines`, except a run of 2+
 *  consecutive numbered-option lines becomes clickable `DisambiguationOption`
 *  cards instead of plain text — clicking one sends that option's number as
 *  the next message, exactly as if the user had typed it (the reply's own
 *  closing line already tells them they can type instead: "You can also type
 *  any part of the location."). Live chat only — the read-only
 *  session-history modal keeps `renderActivityReplyLines`, since there's no
 *  live conversation to reply into. */
function renderActivityReplyWithOptions(
  text: string,
  onSelectOption: (optionNumber: string) => void,
  optionsDisabled: boolean,
): React.ReactNode {
  const lines = text.split('\n');
  const overallTone = detectReplyTone(text);
  const nodes: React.ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const group: { number: string; label: string }[] = [];
    let j = i;
    while (j < lines.length) {
      const m = DISAMBIGUATION_OPTION_RE.exec(lines[j].trim());
      if (!m) break;
      group.push({ number: m[1], label: m[2] });
      j++;
    }
    if (group.length >= 2) {
      nodes.push(
        <div key={`opts-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '8px 0' }}>
          {group.map((opt) => (
            <DisambiguationOption
              key={opt.number}
              number={opt.number}
              label={opt.label}
              disabled={optionsDisabled}
              onSelect={() => onSelectOption(opt.number)}
            />
          ))}
        </div>,
      );
      i = j;
      continue;
    }
    nodes.push(renderReplyLine(line, i, i === lines.length - 1, overallTone));
    i++;
  }
  return nodes;
}

/** Renders `**bold**` spans within a single line. Only ever fed `WELCOME_ADD_NEW`
 * below (authored copy we control), never a live reply — see the "never run
 * through a markdown renderer" note above, which is about backend text. */
function renderInlineBold(line: string): React.ReactNode {
  return line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part,
  );
}

/** The Activity Assistant's static welcome message is the one exception to
 * "plain text, never markdown" above — it's copy we author, not backend
 * output, so there's no risk of stray `*`/`•` characters in a live reply
 * getting misread as markup. Renders `\n\n`-separated blocks as paragraphs,
 * except a block whose every line starts with "• ", which renders as a
 * bullet list with `**bold**` spans resolved per item. */
function renderWelcomeMessage(text: string): React.ReactNode {
  return text.split('\n\n').map((block, bi) => {
    const lines = block.split('\n');
    if (lines.every((line) => line.startsWith('• '))) {
      return (
        <ul key={bi} style={{ margin: bi === 0 ? 0 : '8px 0 0', paddingLeft: 18 }}>
          {lines.map((line, li) => <li key={li}>{renderInlineBold(line.slice(2))}</li>)}
        </ul>
      );
    }
    return (
      <div key={bi} style={{ marginTop: bi === 0 ? 0 : 8 }}>
        {lines.map((line, li) => (
          <React.Fragment key={li}>
            {line}
            {li < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </div>
    );
  });
}

// ─── Chat panel ───────────────────────────────────────────────────────────────

type ConfirmRow = RegionalLibraryItem & { currentRate: number; newRate: number };

/** Activity Assistant only ("addNew" mode) — the cost-impact preview for the
 * *current* pending operation, reported to the caller so it can drive the
 * main tree view into the same diff mode "Modify Existing Activity" uses.
 * `'idle'` means there is nothing pending right now (cleared, answered, or a
 * reply came back with no pending operation) — the caller should drop any
 * diff it's showing. `'loading'`/`'error'` intentionally carry no `data`. */
export interface AddNewActivityPreviewState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  data?: MutationPreviewResponse;
}

/** Activity Assistant only — identity of the activity a just-confirmed "add"
 *  operation created, reported so the tree view can auto-expand to and
 *  briefly highlight it once the refetch it triggers lands (`onActivityAdded`
 *  below) — the same idea as Bill Summaries' `onNodeAdded`, but here the new
 *  activity's real numeric id isn't known client-side (it's assigned
 *  server-side and `CensoActivityCompletedOperation.backend_response` is
 *  opaque) — only `code` is, from the mutation preview that ran just before
 *  confirmation. The caller resolves the real id by matching `code` against
 *  each leaf's own `c` once the target page group's leaves query refetches.
 *  `sectionId`/`pageGroupId` come from that same preview's own parent chain
 *  (`MutationPreviewLeaf.parents`, ordered `[pageGroupParent, sectionParent,
 *  rootParent]`). */
export interface JustAddedActivityInfo {
  code: string;
  sectionId?: number;
  pageGroupId?: number;
  /** The preview's whole `parents` chain, immediate parent first. When the new
   *  activity is placed under a nested row (e.g. Drives → Drives) the chain has
   *  extra entries, so `pageGroupId`/`sectionId` (positional) can point at that
   *  nested row instead — consumers match this chain against the tree instead. */
  ancestors?: { id: number; code: string }[];
}

export interface ChatPanelProps {
  items: RegionalLibraryItem[];
  region: string;
  siteName?: string;
  /** Verified opportunity number(s) disambiguating `siteName` (see `useSiteOppNo`). */
  oppNo?: string;
  tenderName?: string;
  userEmail: string;
  adjustedRates: Record<string, number>;
  onAdjust: (updated: Record<string, number>, summary: string) => void;
  onApply: (savedChanges: CensoChange[]) => void;
  onSessionIdChange: (id: string) => void;
  onDiscardedCodesChange: (codes: Set<string>) => void;
  onChangesReceived?: (changes: CensoChange[]) => void;
  isSimulating?: boolean;
  hideApplyBanner?: boolean;
  onMinimize?: () => void;
  /** 'modify' (default) — the existing rate-adjustment conversation. 'addNew' — the
   * "Add New Activity" conversation (see `CensoAssistantMode`). */
  mode?: CensoAssistantMode;
  /** Required when `mode === 'addNew'` — the project this new activity belongs to,
   * read from the library info endpoint's `projectId`. */
  projectId?: number;
  /** Activity Assistant only — called whenever a confirmed write comes back with
   * `status: 'ok'` in `completed_operations`, so the caller can refetch the
   * activity tree and see the change immediately (an "error"/"cancelled" result
   * doesn't call this, since nothing actually changed). */
  onActivityChanged?: () => void;
  /** Activity Assistant only — fires once, right after `onActivityChanged`,
   * when the just-confirmed write was a genuinely new activity (an `add`
   * operation) rather than a modify/delete/move — so the caller can
   * auto-expand to and highlight it. See `JustAddedActivityInfo`. */
  onActivityAdded?: (info: JustAddedActivityInfo) => void;
  /** Activity Assistant only — fires on every change to the current pending
   * operation's cost preview (loading/ready/error/idle), so the caller can
   * mirror it into the main tree view's diff mode. See `AddNewActivityPreviewState`. */
  onMutationPreview?: (state: AddNewActivityPreviewState) => void;
  /** Whether the panel is currently visible to the user (the caller keeps it
   * mounted but toggles this via CSS when minimising, rather than unmounting
   * it) — used to auto-focus the input whenever the panel is opened/restored.
   * Defaults to `true` for callers that don't track a minimised state. */
  isOpen?: boolean;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({
  items,
  region,
  siteName,
  oppNo,
  tenderName,
  userEmail,
  onAdjust,
  onApply,
  onSessionIdChange,
  onDiscardedCodesChange,
  onChangesReceived,
  isSimulating,
  hideApplyBanner,
  onMinimize,
  mode = 'modify',
  projectId,
  onActivityChanged,
  onActivityAdded,
  onMutationPreview,
  isOpen = true,
}) => {
  const isAddNewMode = mode === 'addNew';
  const sessionCacheKey = censoSessionCacheKey(region, siteName, mode);
  const [sessionId, setSessionId] = useState<string>(() => {
    const cached = censoSessionCache.get(sessionCacheKey);
    if (cached) return cached;
    const fresh = crypto.randomUUID();
    censoSessionCache.set(sessionCacheKey, fresh);
    return fresh;
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);

  const [pendingChanges, setPendingChanges] = useState<Map<string, CensoChange>>(new Map());
  const [savedRatesMap, setSavedRatesMap] = useState<Map<string, number>>(new Map());

  const [messages, setMessages] = useState<ChatMessage[]>(() => [isAddNewMode ? WELCOME_ADD_NEW : WELCOME]);
  // Kept in sync so `sendMessage` (a stable-ish callback whose deps don't
  // include `messages`) can read the latest transcript without being
  // recreated on every message — needed to recover the pending operations a
  // turn was answering if that turn's request then fails (see the catch
  // block's transient-failure branch below).
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [input, setInput] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const rateLimitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoConnectTriggered = useRef(false);
  const lastSentMessageRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  // Stashed identity of the current pending "add" operation's previewed
  // activity — set while it's still awaiting an accept/discard, read (and cleared)
  // once the answering turn's `completed_operations` confirms it. See
  // `JustAddedActivityInfo`'s own doc comment for why this can't just be read
  // off the confirm response directly.
  const pendingAddPreviewRef = useRef<JustAddedActivityInfo | null>(null);

  const queryClient = useQueryClient();
  // Connect is handled with a plain async call + local state rather than
  // useMutation: confirmed via diagnostic logging that the underlying
  // axios request reliably resolves (200) on SPA route navigation, but
  // useMutation's per-call onSuccess/onError callbacks (and isPending) never
  // fired afterwards — leaving the panel stuck on "Connecting to Censo…"
  // even though the session was actually established. Root cause inside
  // TanStack Query's mutation observer wasn't identified; bypassing it here
  // removes the unreliable layer for this one call.
  const [isConnecting, setIsConnecting] = useState(false);
  const isConnectingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);
  const saveMutation = useSaveLibraryChanges();
  const textAreaRef = useRef<React.ComponentRef<typeof Input.TextArea>>(null);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'timestamp'>): number => {
    const id = Date.now() + Math.random();
    setMessages((prev) => [...prev, { ...msg, id, timestamp: new Date() }]);
    return id;
  }, []);

  // Fires a cost-impact preview for a just-received message's *first* pending
  // operation and reports it to `onMutationPreview`, so the caller can mirror
  // it into the main tree view's diff mode (see activity-tree.tsx's
  // `buildSimulateResultFromMutationPreview`) — the tree view can only
  // sensibly highlight one location at a time, and a reply carries more than
  // one pending operation only for a rare compound edit, so later ones are
  // left unpreviewed here. Preview failures never block the plain-text
  // summary or the Yes/No buttons — this is a dry-run add-on, not a
  // precondition for confirming. `msgId` only drives the chat's own
  // "Calculating cost impact…" spinner (`mutationPreviewLoading`) — the
  // preview result itself is reported to `onMutationPreview`, not stored here.
  const previewFirstPendingOperation = useCallback((msgId: number, ops: CensoActivityPendingOperation[]) => {
    const op = ops[0];
    if (!op) return;
    onMutationPreview?.({ status: 'loading' });
    setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, mutationPreviewLoading: true } : m)));

    const clearLoading = () => {
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, mutationPreviewLoading: false } : m)));
    };

    rateEngineApi.previewActivityMutation({
      operation_type: op.operation_type as MutationOperationType,
      operation_place: op.operation_place as MutationOperationPlace,
      placement_key: op.placement_key,
      target_node_handle: op.target_node_handle,
      payload: op.payload,
      session_id: sessionId,
    })
      .then((data) => {
        onMutationPreview?.({ status: 'ready', data });
        clearLoading();
        // Stash this preview's identity only for an 'add' op with a real
        // `primary` leaf — a modify/delete/move (or an add whose preview
        // failed to resolve a primary leaf) has nothing to highlight later.
        pendingAddPreviewRef.current =
          op.operation_type === 'add' && data.primary
            ? {
                code: data.primary.code,
                pageGroupId: data.primary.parents[0]?.activityId,
                sectionId: data.primary.parents[1]?.activityId,
                ancestors: data.primary.parents.map(par => ({ id: par.activityId, code: par.code })),
              }
            : null;
        // TEMP diagnostics for the "just added" highlight feature — remove
        // once confirmed working against the real backend. Logs exactly what
        // the preview returned for an 'add', since the highlight's whole
        // derivation depends on `data.primary`/`.parents` having the shape
        // `applyLeaf` (above) already assumes for the diff-mode preview.
        if (op.operation_type === 'add') {
          // eslint-disable-next-line no-console
          console.debug('[ActivityAssistant][justAdded] preview resolved for add', {
            hasPrimary: !!data.primary,
            primaryCode: data.primary?.code,
            primaryActivityId: data.primary?.activityId,
            parents: data.primary?.parents,
            stashed: pendingAddPreviewRef.current,
          });
        }
      })
      .catch(() => { onMutationPreview?.({ status: 'error' }); clearLoading(); pendingAddPreviewRef.current = null; });
  }, [sessionId, onMutationPreview]);

  // ─── Auto-connect when library items first become available ───────────────

  const doConnect = useCallback(
    (sid: string, currentItems: RegionalLibraryItem[], currentSavedRates: Map<string, number>) => {
      if (isConnectingRef.current) return;

      const rawItems = currentItems.map((item) => ({
        c: item.code,
        n: item.name,
        rg: item.resourceGroup as string,
        r: currentSavedRates.get(item.code) ?? item.rate,
        u: item.unit,
        f: item.isFavourite,
      }));
      isConnectingRef.current = true;
      setIsConnecting(true);
      rateEngineApi.censoConnect({ user_id: userEmail, session_id: sid, items: rawItems })
        .then(() => {
          setIsConnected(true);
          onSessionIdChange(sid);
        })
        .catch((err) => {
          autoConnectTriggered.current = false;
          const isInvalid = isAxiosError(err) && err.response?.status === 400;
          addMessage({
            role: 'assistant',
            text: isInvalid
              ? 'Could not start AI session — invalid request.'
              : 'Could not connect to AI service. Click "Retry connection" to try again.',
            retryConnect: !isInvalid,
          });
        })
        .finally(() => {
          isConnectingRef.current = false;
          setIsConnecting(false);
        });
    },
    [userEmail, addMessage, onSessionIdChange],
  );

  useEffect(() => {
    if (isAddNewMode) return;
    // Wait for both library items AND the user context to be ready — otherwise
    // the connect payload's `user_id` would be `''` and the Censo session would
    // be associated with no user.
    if (items.length > 0 && userEmail && !autoConnectTriggered.current) {
      autoConnectTriggered.current = true;
      doConnect(sessionId, items, savedRatesMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddNewMode, items.length, userEmail]);

  // "Add New Activity" mode (the Activity Assistant) has no connect-then-chat
  // handshake — every turn, including the first, is sent directly via
  // `rateEngineApi.censoActivityChat`. The panel is "connected" as soon as the
  // project id (fetched by the caller from the library info endpoint) and the
  // user's identity are both available.
  useEffect(() => {
    if (!isAddNewMode) return;
    const ready = !!projectId && !!userEmail;
    setIsConnected(ready);
    if (ready) onSessionIdChange(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddNewMode, projectId, userEmail]);

  useEffect(() => {
    return () => {
      if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (messages.length <= 1) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Rate limiting ────────────────────────────────────────────────────────

  const applyRateLimit = useCallback(() => {
    setIsRateLimited(true);
    addMessage({
      role: 'assistant',
      text: "You're sending messages too quickly. Please wait a moment before trying again.",
    });
    if (rateLimitTimerRef.current) clearTimeout(rateLimitTimerRef.current);
    rateLimitTimerRef.current = setTimeout(() => setIsRateLimited(false), 60_000);
  }, [addMessage]);

  // ─── Session expiry (library assistant / "modify" mode only) ──────────────

  const handleSessionExpired = useCallback(() => {
    setIsConnected(false);
    setPendingChanges(new Map());
    addMessage({
      role: 'assistant',
      text: 'Your AI session expired. Reconnecting with a fresh session…',
    });
    const newSessionId = crypto.randomUUID();
    censoSessionCache.set(sessionCacheKey, newSessionId);
    setSessionId(newSessionId);
    doConnect(newSessionId, items, savedRatesMap);
  }, [addMessage, doConnect, items, savedRatesMap, sessionCacheKey]);

  // ─── Activity Assistant: end / restart conversation ────────────────────────
  // Reset is hygiene, not correctness (per the Activity Assistant contract): it
  // discards server-side state (resolved matches, mid-clarification state, any
  // unconfirmed pending change). Best-effort — nothing the user can act on if
  // it fails, so errors are swallowed here.
  const sessionIdRef = useRef(sessionId);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Called when the user "walks away" from an Add New Activity conversation
  // (minimising the panel, or explicitly clicking "Start Over") — resets the
  // old session on the backend and mints a fresh one + clears the transcript,
  // so the next time this mode is opened it starts clean rather than resuming
  // a session the backend has already discarded. The new session starts
  // immediately either way (reset is hygiene, not a precondition for it), but
  // the input is only auto-focused once the reset call actually confirms —
  // not optimistically — so focus lands after the round-trip settles rather
  // than racing it.
  const startFreshActivitySession = useCallback(() => {
    const idToReset = sessionId;
    const newSessionId = crypto.randomUUID();
    censoSessionCache.set(sessionCacheKey, newSessionId);
    setSessionId(newSessionId);
    setMessages([WELCOME_ADD_NEW]);
    onMutationPreview?.({ status: 'idle' });
    pendingAddPreviewRef.current = null;
    rateEngineApi.censoActivityReset(idToReset)
      .then(() => { textAreaRef.current?.focus(); })
      .catch(() => {});
  }, [sessionCacheKey, sessionId, onMutationPreview]);

  // Backstop for navigating away / closing the tab without minimising first.
  useEffect(() => {
    if (!isAddNewMode) return;
    return () => {
      rateEngineApi.censoActivityReset(sessionIdRef.current).catch(() => {});
    };
  }, [isAddNewMode]);

  // ─── Send message ─────────────────────────────────────────────────────────

  const sendMessage = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || !isConnected || isSending || isRateLimited || saveMutation.isPending) return;

    // Snapshot the pending operations (if any) this turn is answering, from
    // *before* the user's message is appended below — needed to re-show the
    // same confirmation if the request fails transiently (see catch block).
    const priorLastMessage = messagesRef.current[messagesRef.current.length - 1];
    const priorPendingOps = isAddNewMode && priorLastMessage?.role === 'assistant'
      ? priorLastMessage.pendingOperations
      : undefined;

    lastSentMessageRef.current = text;
    addMessage({ role: 'user', text });
    if (overrideText === undefined) setInput('');
    setIsSending(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      if (isAddNewMode) {
        if (!projectId) throw new Error('Missing project id for Add New Activity');
        const data = await rateEngineApi.censoActivityChat({
          session_id: sessionId,
          message: text,
          project_id: projectId,
          user_id: userEmail,
          tender_name: tenderName,
          ...(tenderName && oppNo ? { opp_no: oppNo } : {}),
          signal: controller.signal,
        });
        abortControllerRef.current = null;
        const pendingOps = data.pending_operations?.length ? data.pending_operations : undefined;
        const msgId = addMessage({ role: 'assistant', text: data.reply, pendingOperations: pendingOps });
        // Preview the cost impact of the pending operation before the user
        // accepts/discards it — a dry run against /activities/mutate/preview,
        // mirroring the "Modify Existing Rates" simulate-before-confirm flow.
        // The preview itself renders in the main tree view's diff mode, not
        // here — this only shows a "calculating…" spinner in the chat meanwhile.
        if (pendingOps) {
          previewFirstPendingOperation(msgId, pendingOps);
        } else {
          // Nothing pending in this reply (answered, cancelled, or just a plain
          // reply) — drop any diff the caller might still be showing.
          onMutationPreview?.({ status: 'idle' });
        }
        // A confirmed write just landed — refetch the activity tree so the
        // change (e.g. a newly added activity) shows up immediately, without
        // waiting for the user to manually refresh anything.
        if (data.completed_operations?.some((op) => op.status === 'ok')) {
          onActivityChanged?.();
          if (pendingAddPreviewRef.current) onActivityAdded?.(pendingAddPreviewRef.current);
          // eslint-disable-next-line no-console
          console.debug('[ActivityAssistant][justAdded] confirm completed', {
            completedOperations: data.completed_operations,
            stashedAddPreview: pendingAddPreviewRef.current,
          });
        }
        // This turn resolved the previously-pending question one way or
        // another (confirmed, declined, or errored) rather than raising a
        // new one — nothing left to highlight next time, so drop the stash
        // instead of letting it leak into an unrelated later "add".
        if (!pendingOps) pendingAddPreviewRef.current = null;
        return;
      }

      const data = await rateEngineApi.censoChat({ session_id: sessionId, message: text, signal: controller.signal });

      abortControllerRef.current = null;
      addMessage({ role: 'assistant', text: data.reply });

      if (data.changes?.changes?.length) {
        const incoming = data.changes.changes;

        // Compute accumulated state directly (safe: isSending blocks concurrency)
        const nextMap = new Map(pendingChanges);
        incoming.forEach((change) => {
          const existing = nextMap.get(change.c);
          nextMap.set(change.c, {
            c: change.c,
            n: change.n,
            or: existing?.or ?? savedRatesMap.get(change.c) ?? change.or,
            nr: change.nr,
          });
        });

        const adjusted: Record<string, number> = {};
        nextMap.forEach((c, code) => { adjusted[code] = c.nr; });
        const count = nextMap.size;

        setPendingChanges(nextMap);
        onAdjust(adjusted, `AI (${count} item${count !== 1 ? 's' : ''} pending)`);
        onChangesReceived?.(Array.from(nextMap.values()));
      }
    } catch (err) {
      abortControllerRef.current = null;

      const isTimeout = isAxiosError(err) && err.code === 'ECONNABORTED';
      const isCancelled = axios.isCancel(err) || (isAxiosError(err) && err.code === 'ERR_CANCELED');
      const axiosStatus = isAxiosError(err) ? err.response?.status : undefined;
      // No `response` at all (not a timeout/cancel) means the request never
      // got a reply — a genuine "can't reach the backend" failure — and 502/503
      // are the AI service's own "try again shortly/later" signals. All three
      // are transient in the same sense a stalled request is.
      const isTransientFailure = isTimeout
        || (isAxiosError(err) && !isCancelled && (axiosStatus === undefined || axiosStatus === 502 || axiosStatus === 503));

      // Activity Assistant: if the turn that just failed was the user
      // answering a pending confirmation (accept/discard), re-show that same
      // confirmation instead of falling back to free text — this is what lets
      // a reply like "Can't reach the project backend right now — Accept
      // again to retry the rest." actually be actioned by clicking Accept
      // again. The text input stays disabled the whole time via
      // `hasPendingConfirmation`.
      if (isAddNewMode && priorPendingOps?.length && isTransientFailure) {
        setInput('');
        const text = isTimeout
          ? 'The AI took too long to respond. Please try again.'
          : axiosStatus === 502
            ? 'The AI service is temporarily unavailable. Please try again shortly.'
            : axiosStatus === 503
              ? 'The AI service is currently busy, or is switched off in this environment. Please try again later.'
              : "Can't reach the project backend right now — please try again.";
        addMessage({ role: 'assistant', text, pendingOperations: priorPendingOps });
        return;
      }

      setInput(lastSentMessageRef.current);

      if (isTimeout) {
        addMessage({ role: 'assistant', text: 'The AI took too long to respond. Your message has been restored — please try again.' });
      } else if (isCancelled) {
        addMessage({ role: 'assistant', text: 'The request was cancelled. Your message has been restored — please try again.' });
      } else if (isAddNewMode && isAxiosError(err)) {
        // Activity Assistant errors come back as `{ error: "..." }`, not `{ message }`,
        // and this endpoint has its own status meanings (no 404/session-expiry here).
        const status = err.response?.status;
        const serverError = (err.response?.data as { error?: string } | undefined)?.error;
        if (status === 429) {
          applyRateLimit();
        } else if (status === 409) {
          // A 409 carries two different bodies: the project-scope conflict
          // (`{ error }`), and a normal chat payload when a confirmed write was
          // rejected (e.g. ALREADY_ON_BILL_LINE) — `reply` explains why and
          // `completed_operations` holds the failed op. That payload still lists
          // the rejected op under `pending_operations`; re-showing it would lock
          // the input behind an Accept/Discard that can only fail again, so it's
          // deliberately dropped and the conversation stays open.
          const body = err.response?.data as Partial<CensoActivityChatResponse> | undefined;
          if (body?.reply) {
            setInput('');
            addMessage({ role: 'assistant', text: body.reply });
            onMutationPreview?.({ status: 'idle' });
            pendingAddPreviewRef.current = null;
          } else {
            addMessage({
              role: 'assistant',
              text: serverError ?? 'This conversation is scoped to a different project. Start a new conversation to switch projects.',
            });
          }
        } else if (status === 400) {
          addMessage({ role: 'assistant', text: serverError ?? 'Something required was missing from the request. Please try again.' });
        } else if (status === 502) {
          addMessage({ role: 'assistant', text: 'The AI service is temporarily unavailable. Please try again shortly.' });
        } else if (status === 503) {
          addMessage({ role: 'assistant', text: 'The AI service is currently busy, or is switched off in this environment. Please try again later.' });
        } else {
          addMessage({ role: 'assistant', text: serverError ?? 'Something went wrong. Please try again.' });
        }
      } else if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 429) {
          applyRateLimit();
        } else if (status === 404) {
          handleSessionExpired();
        } else if (status === 502) {
          addMessage({ role: 'assistant', text: 'The AI service is temporarily unavailable. Please try again shortly.' });
        } else if (status === 503) {
          addMessage({ role: 'assistant', text: 'The AI service is currently busy. Please try again later.' });
        } else {
          addMessage({ role: 'assistant', text: 'Something went wrong. Please try again.' });
        }
      } else {
        addMessage({ role: 'assistant', text: 'Something went wrong. Please try again.' });
      }
    } finally {
      setIsSending(false);
    }
  }, [
    input, isConnected, isSending, isRateLimited, saveMutation.isPending,
    sessionId, pendingChanges, savedRatesMap, addMessage, onAdjust, onChangesReceived,
    applyRateLimit, handleSessionExpired, isAddNewMode, projectId, userEmail, onActivityChanged,
    onActivityAdded, previewFirstPendingOperation, onMutationPreview,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Activity Assistant confirmation — there is no separate confirm endpoint;
  // the user's "accept"/"discard" is just the next chat message.
  const answerPendingOperation = useCallback((answer: 'accept' | 'discard') => {
    sendMessage(answer);
  }, [sendMessage]);

  const handleAbort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  // ─── Apply / save ─────────────────────────────────────────────────────────

  const handleApplyClick = () => setConfirmOpen(true);

  const handleDiscard = useCallback(() => {
    setPendingChanges(new Map());
    onAdjust({}, '');
    addMessage({ role: 'assistant', text: 'Pending rate changes have been discarded. Starting a new session…' });
    // The old session still holds the discarded changes server-side — start a
    // fresh one so the next request isn't built on top of them.
    setIsConnected(false);
    const newSessionId = crypto.randomUUID();
    censoSessionCache.set(sessionCacheKey, newSessionId);
    setSessionId(newSessionId);
    doConnect(newSessionId, items, savedRatesMap);
  }, [onAdjust, addMessage, doConnect, items, savedRatesMap, sessionCacheKey]);

  const handleConfirm = useCallback(() => {
    const changes = Array.from(pendingChanges.values());
    setConfirmOpen(false);

    saveMutation.mutate(
      { region, request: { changedBy: userEmail, sessionId, changes, tenderName }, siteName, oppNo },
      {
        onSuccess: (result) => {
          addMessage({
            role: 'assistant',
            text: `Saved! ${result.ac} rate${result.ac !== 1 ? 's' : ''} updated in the ${result.pN}.${result.isNew ? ' A new site project was created.' : ''}`,
          });

          setSavedRatesMap((prev) => {
            const next = new Map(prev);
            changes.forEach((c) => next.set(c.c, c.nr));
            return next;
          });

          setPendingChanges(new Map());

          queryClient.invalidateQueries({
            queryKey: RATE_ENGINE_QUERY_KEYS.libraryHistory(region, siteName, undefined, oppNo),
          });

          onApply(changes);
        },
        onError: (err) => {
          if (isAxiosError(err) && err.response?.status === 409) {
            addMessage({
              role: 'assistant',
              text: 'Some rates have been changed by another user. Reloading the latest data…',
            });
            queryClient.invalidateQueries({
              queryKey: RATE_ENGINE_QUERY_KEYS.libraryItems(region, siteName, oppNo),
            });
          } else if (isAxiosError(err) && err.response?.status === 400) {
            addMessage({
              role: 'assistant',
              text: 'Save failed — one or more changes were invalid. Please review and try again.',
            });
          } else {
            addMessage({ role: 'assistant', text: 'Failed to save changes. Please try again.' });
          }
        },
      },
    );
  }, [pendingChanges, region, userEmail, sessionId, siteName, oppNo, saveMutation, addMessage, queryClient, onApply]);

  // ─── Derived state ────────────────────────────────────────────────────────

  const discardedCodesRef = React.useRef<Set<string>>(new Set());

  const handleDiscardItem = useCallback((code: string) => {
    setPendingChanges((prev) => {
      const next = new Map(prev);
      next.delete(code);
      if (next.size === 0) {
        setConfirmOpen(false);
        onAdjust({}, '');
      } else {
        const adjusted: Record<string, number> = {};
        next.forEach((c, k) => { adjusted[k] = c.nr; });
        onAdjust(adjusted, `AI (${next.size} item${next.size !== 1 ? 's' : ''} pending)`);
      }
      return next;
    });
    const next = new Set([...discardedCodesRef.current, code]);
    discardedCodesRef.current = next;
    onDiscardedCodesChange(next);
  }, [onAdjust, onDiscardedCodesChange]);

  // Drive the rows from `pendingChanges` (source of truth) and join the
  // regional-library record on the side. Earlier this iterated `items` and
  // filtered to those whose code was in `pendingChanges`, which silently
  // dropped any AI change whose code wasn't in the regional library list
  // (e.g. `SPOIL0003A`) — the user couldn't review/discard it, yet
  // `handleConfirm` would still save it because it pulls straight from
  // `pendingChanges`. Off-library codes now show with their `n` (name) and
  // resourceGroup="Other" fallback so they're visible alongside library
  // items in both the inline pending-changes table and the Apply-confirm
  // modal.
  const itemByCode = useMemo(
    () => new Map(items.map((item) => [item.code, item])),
    [items],
  );
  const pendingRows: ConfirmRow[] = Array.from(pendingChanges.values()).map(
    (change) => {
      const item = itemByCode.get(change.c);
      if (item) {
        return { ...item, currentRate: change.or, newRate: change.nr };
      }
      return {
        code: change.c,
        name: change.n,
        resourceGroup: 'Other' as ResourceGroup,
        rate: change.or,
        unit: '',
        isFavourite: false,
        currentRate: change.or,
        newRate: change.nr,
      };
    },
  );

  const hasAdjustments = pendingChanges.size > 0;
  const isSaving = saveMutation.isPending;

  // Activity Assistant only — the pending operation(s) on the most recent
  // assistant message, if any. Drives the bottom confirm bar below (mirroring
  // "Modify Existing Activity"'s Apply banner) instead of inline chat buttons.
  const lastMessage = messages[messages.length - 1];
  const pendingConfirmation = isAddNewMode && lastMessage?.role === 'assistant'
    ? lastMessage.pendingOperations
    : undefined;
  const hasPendingConfirmation = !!pendingConfirmation?.length;
  const confirmationLoading = !!lastMessage?.mutationPreviewLoading;

  // While a Discard/Accept confirmation is pending, the buttons are the only
  // valid response — typing "accept"/"discard" as free text hits the same
  // sendMessage path but isn't guaranteed to be understood the same way
  // server-side, so block typing until the user picks Discard or Accept.
  const inputDisabled = !isConnected || isSending || isRateLimited || isSaving || hasPendingConfirmation;

  // Auto-focus the input once a response comes back, so the user can type the
  // next message immediately without clicking back into the box. Only fires on
  // the true→false transition (a reply just arrived), not on every render.
  const wasSendingRef = useRef(isSending);
  useEffect(() => {
    const wasSending = wasSendingRef.current;
    wasSendingRef.current = isSending;
    if (wasSending && !isSending && !inputDisabled && !confirmOpen) {
      textAreaRef.current?.focus();
    }
  }, [isSending, inputDisabled, confirmOpen]);

  // Auto-focus when the panel is opened/restored from minimised. The panel
  // stays mounted while minimised (only its CSS display toggles), so this
  // can't be a mount-only effect — it has to react to `isOpen` itself.
  useEffect(() => {
    if (!isOpen) return;
    const id = requestAnimationFrame(() => textAreaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [isOpen]);

  const confirmColumns: ColumnsType<ConfirmRow> = [
    { title: 'Code', dataIndex: 'code', key: 'code', width: 110 },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Group',
      dataIndex: 'resourceGroup',
      key: 'resourceGroup',
      width: 110,
      render: (rg: ResourceGroup) => <Tag color={RESOURCE_GROUP_COLOURS[rg]}>{rg}</Tag>,
    },
    {
      title: 'Current (£)',
      dataIndex: 'currentRate',
      key: 'currentRate',
      width: 100,
      align: 'right',
      render: (v: number) =>
        v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      title: 'New Rate (£)',
      dataIndex: 'newRate',
      key: 'newRate',
      width: 110,
      align: 'right',
      render: (v: number, record: ConfirmRow) => {
        const diff = v - record.currentRate;
        return (
          <span style={{ color: diff > 0 ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
            {v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
    {
      title: 'Change',
      key: 'change',
      width: 80,
      align: 'right',
      render: (_: unknown, record: ConfirmRow) => {
        const pct = ((record.newRate - record.currentRate) / record.currentRate) * 100;
        const sign = pct >= 0 ? '+' : '';
        return (
          <span style={{ color: pct >= 0 ? '#52c41a' : '#ff4d4f', fontSize: '12px' }}>
            {sign}{pct.toFixed(1)}%
          </span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 70,
      align: 'center',
      render: (_: unknown, record: ConfirmRow) => (
        <Button
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          danger
          title="Discard this change"
          onClick={() => handleDiscardItem(record.code)}
        />
      ),
    },
  ];

  const statusLabel = isConnecting
    ? 'Connecting to Censo…'
    : !isConnected
      ? (isAddNewMode ? 'Loading project info…' : 'AI service offline')
      : hasAdjustments
        ? `${pendingChanges.size} rate${pendingChanges.size !== 1 ? 's' : ''} pending — click Apply to save`
        : isAddNewMode
          ? 'Ask me to find or change an activity'
          : 'Ask me to adjust rates';

  const panelTitle = isAddNewMode ? 'Activity Assistant' : 'Rate Assistant';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#fff',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 14px',
          background: 'linear-gradient(135deg, #0B2545 0%, #1c3f72 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexShrink: 0,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {/* Avatar + status dot */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            size={36}
            icon={
              isConnecting
                ? <Spin indicator={<LoadingOutlined style={{ color: '#fff', fontSize: 16 }} />} />
                : <RobotOutlined />
            }
            style={{
              backgroundColor: 'rgba(255,255,255,0.12)',
              border: '1.5px solid rgba(255,255,255,0.2)',
            }}
          />
          <span style={{
            position: 'absolute', bottom: 1, right: 1,
            width: 9, height: 9, borderRadius: '50%',
            backgroundColor: isConnecting ? '#fa8c16' : isConnected ? '#52c41a' : '#ff4d4f',
            border: '2px solid #0B2545',
            display: 'block',
          }} />
        </div>

        {/* Title + status */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: '0.01em', lineHeight: 1.3 }}>
            {panelTitle}
          </div>
          <div style={{
            fontSize: 11, lineHeight: 1.4, marginTop: 1,
            color: isConnecting ? '#ffa940' : isConnected ? '#95de64' : 'rgba(255,255,255,0.45)',
          }}>
            {statusLabel}
          </div>
        </div>

        {/* Start Over — Activity Assistant only: resets the conversation on the
            backend (see the reset endpoint's contract) and starts a fresh session
            without minimising the panel. */}
        {isAddNewMode && (
          <button
            onClick={() => {
              Modal.confirm({
                title: 'Start a new conversation?',
                content: 'This will clear the current conversation. Anything unconfirmed will be lost.',
                okText: 'Start Over',
                cancelText: 'Cancel',
                onOk: () => {
                  handleAbort();
                  startFreshActivitySession();
                },
              });
            }}
            disabled={isSending}
            title="Start a new conversation"
            style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6, cursor: isSending ? 'not-allowed' : 'pointer',
              opacity: isSending ? 0.5 : 1,
              color: 'rgba(255,255,255,0.7)', padding: '3px 6px',
              display: 'flex', alignItems: 'center', gap: 5, lineHeight: 1,
              fontSize: 11, fontWeight: 600,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (!isSending) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)'; }}
          >
            <ReloadOutlined style={{ fontSize: 12 }} />
            Start Over
          </button>
        )}

        {/* Minimise */}
        {onMinimize && (
          <button
            onClick={() => {
              if (isAddNewMode) startFreshActivitySession();
              onMinimize();
            }}
            title="Minimise panel"
            style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6, cursor: 'pointer',
              color: 'rgba(255,255,255,0.7)', padding: '3px 6px',
              display: 'flex', alignItems: 'center', lineHeight: 1,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)'; }}
          >
            <MinusOutlined style={{ fontSize: 12 }} />
          </button>
        )}
      </div>

      {/* Messages */}
      <style>{`
        @keyframes _cp_dot {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-5px); opacity: 1; }
        }
        ._cp_d1 { animation: _cp_dot 1.2s ease-in-out infinite 0s; }
        ._cp_d2 { animation: _cp_dot 1.2s ease-in-out infinite 0.2s; }
        ._cp_d3 { animation: _cp_dot 1.2s ease-in-out infinite 0.4s; }
        ._cp_msgs::-webkit-scrollbar { width: 4px; }
        ._cp_msgs::-webkit-scrollbar-track { background: transparent; }
        ._cp_msgs::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 4px; }
        ._cp_msgs::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.28); }
      `}</style>
      <div
        className="_cp_msgs"
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          backgroundColor: '#f7f8fc',
        }}
      >
        {messages.map((msg) => {
          const isUser = msg.role === 'user';
          // Live chat only (not the session-history modal, which keeps the
          // plain neutral bubble) — a subtle tint + left accent border on top
          // of the existing bubble, so a question/success/error reply reads
          // at a glance without the raw `[?]`/`[OK]`/`[!]` tag visible.
          const tone = isAddNewMode && !isUser ? detectReplyTone(msg.text) : undefined;
          const toneStyle = tone && ACTIVITY_REPLY_TONE_STYLES.find((t) => t.tone === tone);
          return (
            <div
              key={msg.id}
              style={{
                display: 'flex',
                flexDirection: isUser ? 'row-reverse' : 'row',
                alignItems: 'flex-start',
                gap: '8px',
              }}
            >
              <Avatar
                size={30}
                icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                style={{
                  backgroundColor: isUser ? '#1890ff' : '#0B2545',
                  flexShrink: 0,
                  marginTop: 2,
                }}
              />
              <div
                style={{
                  maxWidth: '76%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isUser ? 'flex-end' : 'flex-start',
                  gap: '3px',
                }}
              >
                <span style={{ fontSize: 10, fontWeight: 600, color: '#999', paddingLeft: isUser ? 0 : 2, paddingRight: isUser ? 2 : 0 }}>
                  {isUser ? 'You' : panelTitle}
                </span>
                <div
                  style={{
                    padding: '9px 13px',
                    borderRadius: isUser ? '18px 4px 18px 18px' : '4px 18px 18px 18px',
                    backgroundColor: isUser ? '#0B2545' : (toneStyle?.tint ?? '#fff'),
                    color: isUser ? '#fff' : '#1a1a1a',
                    fontSize: '13px',
                    lineHeight: 1.65,
                    boxShadow: isUser
                      ? '0 2px 6px rgba(11,37,69,0.25)'
                      : '0 1px 3px rgba(0,0,0,0.10)',
                    border: isUser ? 'none' : '1px solid rgba(0,0,0,0.07)',
                    borderLeft: isUser ? undefined : `3px solid ${toneStyle?.accent ?? 'rgba(0,0,0,0.07)'}`,
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                  }}
                >
                  {isAddNewMode && !isUser
                    ? (msg.id === 0
                        ? renderWelcomeMessage(msg.text)
                        : renderActivityReplyWithOptions(
                            msg.text,
                            (optionNumber) => sendMessage(optionNumber),
                            inputDisabled || msg.id !== lastMessage?.id,
                          ))
                    : msg.text}
                  {isAddNewMode && !isUser && msg.mutationPreviewLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, color: '#999', fontSize: 11 }}>
                      <Spin size="small" /> Calculating cost impact…
                    </div>
                  )}
                  {msg.retryConnect && (
                    <div style={{ marginTop: 8 }}>
                      <Button
                        size="small"
                        type="primary"
                        onClick={() => {
                          autoConnectTriggered.current = false;
                          doConnect(sessionId, items, savedRatesMap);
                        }}
                        loading={isConnecting}
                        style={{ fontSize: 12 }}
                      >
                        Retry connection
                      </Button>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: '10px', color: '#b0b0b0', paddingLeft: isUser ? 0 : 2, paddingRight: isUser ? 2 : 0 }}>
                  {msg.timestamp.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          );
        })}

        {/* Typing indicator — animated dots bubble */}
        {(isSending || isSimulating) && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <Avatar size={30} icon={<RobotOutlined />} style={{ backgroundColor: '#0B2545', flexShrink: 0, marginTop: 2 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: '#999', paddingLeft: 2 }}>{panelTitle}</span>
              <div style={{
                padding: '10px 16px',
                borderRadius: '4px 18px 18px 18px',
                backgroundColor: '#fff',
                border: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                  {['_cp_d1', '_cp_d2', '_cp_d3'].map(cls => (
                    <span key={cls} className={cls} style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#0B2545', display: 'inline-block' }} />
                  ))}
                </div>
                {isSimulating && !isSending && (
                  <span style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>Calculating impact…</span>
                )}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Apply banner */}
      {hasAdjustments && !hideApplyBanner && (
        <div style={{
          padding: '10px 12px',
          background: 'linear-gradient(90deg, #f0f7ff 0%, #e8f3ff 100%)',
          borderTop: '2px solid #4096ff',
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#0B2545', lineHeight: 1.3 }}>
              {pendingChanges.size} rate{pendingChanges.size !== 1 ? 's' : ''} pending
            </div>
            <div style={{ fontSize: 10, color: '#6b8cba', marginTop: 1 }}>Ready to save to library</div>
          </div>
          <Button size="small" disabled={isSaving} onClick={handleDiscard} style={{ borderRadius: 6 }}>
            Discard
          </Button>
          <Button
            type="primary" size="small" loading={isSaving} onClick={handleApplyClick}
            style={{ backgroundColor: '#0B2545', borderColor: '#0B2545', borderRadius: 6, fontWeight: 600 }}
          >
            Apply
          </Button>
        </div>
      )}

      {/* Confirm bar — Activity Assistant's pending operation. Mirrors the
          Apply banner above (same look, same position) instead of the old
          inline Yes/No buttons inside the chat bubble, so both assistant
          modes end a proposed change the same way: review the diff in the
          tree, then Accept/Discard from the bar at the bottom of the chat. */}
      {isAddNewMode && hasPendingConfirmation && !hideApplyBanner && (
        <div style={{
          padding: '10px 12px',
          background: 'linear-gradient(90deg, #f0f7ff 0%, #e8f3ff 100%)',
          borderTop: '2px solid #4096ff',
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 700, color: '#0B2545', lineHeight: 1.3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {pendingConfirmation![0].summary || 'Confirm this change?'}
            </div>
            <div style={{ fontSize: 10, color: '#6b8cba', marginTop: 1 }}>
              {confirmationLoading ? 'Calculating cost impact…' : 'Review the preview in the tree, then confirm'}
            </div>
          </div>
          <Button
            size="small"
            disabled={isSending || !isConnected || confirmationLoading}
            onClick={() => answerPendingOperation('discard')}
            style={{ borderRadius: 6 }}
          >
            Discard
          </Button>
          <Button
            type="primary" size="small" loading={isSending}
            disabled={!isConnected || confirmationLoading}
            onClick={() => answerPendingOperation('accept')}
            style={{ backgroundColor: '#0B2545', borderColor: '#0B2545', borderRadius: 6, fontWeight: 600 }}
          >
            Accept
          </Button>
        </div>
      )}

      {/* Input */}
      <div style={{ padding: '10px 12px 12px', borderTop: '1px solid #f0f0f0', backgroundColor: '#fff', flexShrink: 0 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#f5f7fa',
          border: '1.5px solid #e4e8f0',
          borderRadius: 22,
          padding: '5px 5px 5px 14px',
        }}>
          <Input.TextArea
            ref={textAreaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isConnecting ? 'Connecting to AI…'
                : !isConnected ? (isAddNewMode ? 'Loading project info…' : 'AI service unavailable')
                : isRateLimited ? 'Please wait a moment…'
                : isAddNewMode ? 'Ask me to find or change an activity…'
                : 'Ask me to adjust rates…'
            }
            disabled={inputDisabled}
            autoSize={{ minRows: 1, maxRows: 4 }}
            variant="borderless"
            style={{ flex: 1, resize: 'none', fontSize: 13, padding: 0, background: 'transparent', boxShadow: 'none' }}
          />
          {isSending ? (
            <button
              onClick={handleAbort}
              title="Stop generating"
              style={{
                width: 34, height: 34, borderRadius: '50%',
                backgroundColor: '#0B2545', border: 'none',
                cursor: 'pointer', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(11,37,69,0.35)',
                transition: 'transform 0.12s ease',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
            >
              <svg viewBox="0 0 24 24" width="13" height="13" fill="#fff">
                <rect x="5" y="5" width="14" height="14" rx="2.5" />
              </svg>
            </button>
          ) : (
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || inputDisabled}
              title="Send message"
              style={{
                width: 34, height: 34, borderRadius: '50%',
                backgroundColor: !input.trim() || inputDisabled ? '#c8cdd8' : '#0B2545',
                border: 'none',
                cursor: !input.trim() || inputDisabled ? 'not-allowed' : 'pointer',
                flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: !input.trim() || inputDisabled ? 'none' : '0 2px 8px rgba(11,37,69,0.35)',
                transition: 'background-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease',
              }}
              onMouseEnter={e => {
                if (!(!input.trim() || inputDisabled)) (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)';
              }}
              onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="#fff">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Confirmation modal */}
      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onOk={handleConfirm}
        confirmLoading={isSaving}
        okText="Yes, Apply"
        cancelText="Cancel"
        okButtonProps={{ style: { backgroundColor: '#52c41a', borderColor: '#52c41a' } }}
        title={
          <span>
            Confirm Rate Update
            <Tag color="orange" style={{ marginLeft: 10 }}>
              {pendingRows.length} item{pendingRows.length !== 1 ? 's' : ''} will change
            </Tag>
          </span>
        }
        width={820}
        styles={{ body: { paddingTop: 12 } }}
      >
        <p style={{ marginBottom: 12, color: '#555' }}>
          The following rates will be saved to the library. Are you sure you want to apply these
          changes?
        </p>
        <Table<ConfirmRow>
          rowKey="code"
          columns={confirmColumns}
          dataSource={pendingRows}
          size="small"
          pagination={pendingRows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
          scroll={{ y: 320 }}
        />
      </Modal>
    </div>
  );
};

// ─── Change History Modal ─────────────────────────────────────────────────────

const isRefreshRecord = (record: LibraryHistoryRecord) =>
  record.actionType?.toLowerCase() === 'refresh';

const ACTION_TYPE_TAG: Record<string, { color: string; label: string }> = {
  insert: { color: 'green', label: 'Insert' },
  create: { color: 'green', label: 'Create' },
  add: { color: 'cyan', label: 'Add' },
  update: { color: 'blue', label: 'Update' },
  modify: { color: 'blue', label: 'Modify' },
  delete: { color: 'red', label: 'Delete' },
  remove: { color: 'red', label: 'Remove' },
  move: { color: 'gold', label: 'Move' },
};
/** Title-cases anything not in the map above, rather than showing the raw
 *  lowercase wire value verbatim, for whatever the real `action`/`type`
 *  vocabulary turns out to include beyond what's confirmed so far. */
const titleCase = (v: string): string => v.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** Fields shown with a £ prefix wherever they appear in a record's before/after. */
const CURRENCY_FIELDS = new Set(['rate']);
/** Fields shown as a plain (non-currency) number wherever they appear. */
const NUMERIC_FIELDS = new Set(['qty', 'multiplyByA', 'multiplyByB', 'divideBy']);

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  multiplyByA: '×A', multiplyByB: '×B', divideBy: '÷', qty: 'Qty', rate: 'Rate',
  unit: 'Unit', name: 'Name', resourceGroup: 'Resource Group', parent: 'Parent',
  parentType: 'Parent Type', parentCode: 'Parent',
};
/** camelCase → "Title Case", with a few overrides for abbreviations/acronyms
 *  a naive split would render awkwardly (e.g. `multiplyByA` → "×A", not
 *  "Multiply By A"). */
const fieldLabel = (key: string): string =>
  FIELD_LABEL_OVERRIDES[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const formatFieldValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  if (CURRENCY_FIELDS.has(key)) {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return isNaN(n) ? String(value) : `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  }
  if (NUMERIC_FIELDS.has(key)) {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return isNaN(n) ? String(value) : n.toLocaleString('en-GB', { maximumFractionDigits: 4 });
  }
  return String(value);
};

/** A field/value snapshot for a create/add/delete record's full before-or-after
 *  state (these action types touch several fields at once, unlike `modify`/
 *  `move`'s single-field before→after diff) — one row per field, label left,
 *  value right, monospace. `fields` isn't reliably an object in practice (the
 *  "empty side" of a create/delete can come back `null` rather than `{}`), so
 *  this tolerates that instead of throwing. */
const SnapshotList: React.FC<{ fields: Record<string, unknown> | null | undefined }> = ({ fields }) => {
  const entries = Object.entries(fields ?? {}).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) {
    return <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>Nothing</div>;
  }
  return (
    <div>
      {entries.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'grid', gridTemplateColumns: '84px minmax(0, 1fr)', alignItems: 'baseline',
            gap: 10, fontSize: 12, padding: '2px 0',
          }}
        >
          <span style={{ color: 'rgba(0,0,0,0.45)', overflowWrap: 'anywhere' }}>{fieldLabel(k)}</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'rgba(0,0,0,0.85)', minWidth: 0, textAlign: 'left', overflowWrap: 'anywhere' }}>
            {formatFieldValue(k, v)}
          </span>
        </div>
      ))}
    </div>
  );
};

/** A single before↔after numeric delta badge, shown next to the After column
 *  when before/after share exactly one key and both sides parse as numbers
 *  (covers `modify`'s rate/factor changes) — purely additive context, never
 *  the only thing rendered (unlike the previous single-field-only "Change"
 *  cell, `SnapshotList` in the Before/After columns already shows every
 *  field on both sides regardless of whether a delta applies). */
const DeltaBadge: React.FC<{ record: LibraryHistoryRecord }> = ({ record }) => {
  const beforeKeys = Object.keys(record.before ?? {});
  const afterKeys = Object.keys(record.after ?? {});
  if (beforeKeys.length !== 1 || afterKeys.length !== 1 || beforeKeys[0] !== afterKeys[0]) return null;
  const key = beforeKeys[0];
  const beforeNum = parseFloat(String((record.before ?? {})[key]));
  const afterNum = parseFloat(String((record.after ?? {})[key]));
  if (isNaN(beforeNum) || isNaN(afterNum) || beforeNum === 0) return null;
  const pct = ((afterNum - beforeNum) / beforeNum) * 100;
  if (pct === 0) return null;
  const color = pct > 0 ? '#389e0d' : '#cf1322';
  const bg = pct > 0 ? '#f6ffed' : '#fff1f0';
  return (
    <span style={{
      color, background: bg, fontWeight: 700, fontSize: 10, letterSpacing: 0, textTransform: 'none',
      padding: '1px 6px', borderRadius: 8,
    }}>
      {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
};

const SNAPSHOT_PANEL_BASE: React.CSSProperties = {
  flex: 1, minWidth: 0, borderRadius: 10, padding: '10px 12px', border: '1px solid transparent',
};

/** The "Change" column's cell — a click-to-open Popover instead of always-on
 *  Before/After columns, so a batch of many records (a typical AI build-out
 *  touches several bill lines/build-ups/items at once) reads as a compact
 *  list first, with full detail one click away rather than every row's full
 *  before/after snapshot competing for width on screen simultaneously. */
const ChangeDetailsPopover: React.FC<{ record: LibraryHistoryRecord }> = ({ record }) => {
  const fieldCount = Object.keys(record.before ?? {}).length + Object.keys(record.after ?? {}).length;
  const actionMeta = ACTION_TYPE_TAG[record.actionType?.toLowerCase()] ?? { color: 'default', label: record.actionType ? titleCase(record.actionType) : 'Unknown' };

  const content = (
    <div style={{ width: 440, maxWidth: '80vw' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 10, marginBottom: 10,
        borderBottom: '1px solid #f0f0f0',
      }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: '#1890ff' }}>
          {record.entityCode || '—'}
        </span>
        <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {record.entityName}
        </span>
        <Tag color={actionMeta.color} style={{ margin: 0 }}>{actionMeta.label}</Tag>
      </div>

      <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
        <div style={{ ...SNAPSHOT_PANEL_BASE, background: '#fff1f0', borderColor: '#ffd8d4' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#cf7a72', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4 }}>
            Before
          </div>
          <SnapshotList fields={record.before} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', color: '#d9d9d9', fontSize: 14 }}>
          <ArrowRightOutlined />
        </div>

        <div style={{ ...SNAPSHOT_PANEL_BASE, background: '#f6ffed', borderColor: '#d3edc2' }}>
          <div style={{
            display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
            fontSize: 10, fontWeight: 700, color: '#7ba668', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4,
          }}>
            <span>After</span>
            <DeltaBadge record={record} />
          </div>
          <SnapshotList fields={record.after} />
        </div>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="left"
      styles={{ body: { padding: 16, borderRadius: 12 } }}
    >
      <Button
        type="default"
        size="small"
        icon={<EyeOutlined />}
        style={{
          borderRadius: 14, height: 24, paddingInline: 10, fontSize: 11,
          display: 'inline-flex', alignItems: 'center', gap: 4,
          color: '#666', borderColor: '#e2e2e2',
        }}
      >
        {fieldCount > 0 ? `${fieldCount} field${fieldCount !== 1 ? 's' : ''}` : 'View'}
      </Button>
    </Popover>
  );
};

function makeBatchColumns(
  discardedIds: Set<number>,
  onDiscard: (id: number) => void,
  onRestore: (id: number) => void,
): ColumnsType<LibraryHistoryRecord> {
  return [
    {
      title: 'Code',
      dataIndex: 'entityCode',
      key: 'entityCode',
      width: 110,
      render: (v: string, record: LibraryHistoryRecord) => {
        const discarded = discardedIds.has(record.id);
        return (
          <span style={{
            fontFamily: 'monospace', fontSize: 12, fontWeight: 600,
            textDecoration: discarded ? 'line-through' : 'none', color: discarded ? '#aaa' : '#1890ff',
          }}>
            {v || '—'}
          </span>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'entityName',
      key: 'entityName',
      render: (v: string, record: LibraryHistoryRecord) => {
        const discarded = discardedIds.has(record.id);
        return (
          <span style={{ textDecoration: discarded ? 'line-through' : 'none', color: discarded ? '#aaa' : 'inherit', fontWeight: 500 }}>
            {v || record.entityCode || '—'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'actionType',
      key: 'actionType',
      width: 90,
      render: (v: string, record: LibraryHistoryRecord) => {
        const discarded = discardedIds.has(record.id);
        const meta = ACTION_TYPE_TAG[v?.toLowerCase()] ?? { color: 'default', label: v ? titleCase(v) : 'Unknown' };
        return (
          <Tag color={meta.color} style={{ margin: 0, opacity: discarded ? 0.5 : 1 }}>
            {meta.label}
          </Tag>
        );
      },
    },
    {
      title: 'Change',
      key: 'change',
      width: 110,
      render: (_: unknown, record: LibraryHistoryRecord) => (
        <span style={{ opacity: discardedIds.has(record.id) ? 0.5 : 1 }}>
          <ChangeDetailsPopover record={record} />
        </span>
      ),
    },
    {
      title: '',
      key: 'rowAction',
      width: 70,
      align: 'center',
      render: (_: unknown, record: LibraryHistoryRecord) => {
        const discarded = discardedIds.has(record.id);
        return discarded ? (
          <Button
            type="text"
            size="small"
            title="Restore this record"
            onClick={() => onRestore(record.id)}
            style={{ color: '#1890ff', fontSize: 11 }}
          >
            Restore
          </Button>
        ) : (
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            danger
            title="Discard this record"
            onClick={() => onDiscard(record.id)}
          />
        );
      },
    },
  ];
}

/** Distinct, read-only column set for site-refresh events (nothing to discard/restore, no before/after rate). */
function makeRefreshColumns(): ColumnsType<LibraryHistoryRecord> {
  return [
    {
      title: 'Action',
      key: 'action',
      render: () => <span style={{ fontWeight: 600 }}>Reset to Regional Defaults</span>,
    },
    {
      title: 'Date/Time',
      dataIndex: 'changedAt',
      key: 'changedAt',
      width: 170,
      align: 'right',
      render: (v: string) => parseUtc(v).toLocaleString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      }),
    },
  ];
}

export interface ChangeHistoryModalProps {
  open: boolean;
  onClose: () => void;
  region: string;
  siteName?: string;
  oppNo?: string;
  items?: RegionalLibraryItem[];
}

export const ChangeHistoryModal: React.FC<ChangeHistoryModalProps> = ({ open, onClose, region, siteName, oppNo, items = [] }) => {
  const { data, isLoading, isFetching, refetch } = useLibraryHistory(region, siteName, undefined, {
    enabled: open && !!region,
  }, oppNo);

  const nameMap = useMemo(() => {
    const m = new Map<string, string>();
    items.forEach((item) => m.set(item.code, item.name));
    return m;
  }, [items]);

  const [activeKeys, setActiveKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [discardedRecordIds, setDiscardedRecordIds] = useState<Set<number>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleDiscardRecord = useCallback((id: number) => {
    setDiscardedRecordIds((prev) => new Set([...prev, id]));
  }, []);

  const handleRestoreRecord = useCallback((id: number) => {
    setDiscardedRecordIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const batchColumns = useMemo(
    () => makeBatchColumns(discardedRecordIds, handleDiscardRecord, handleRestoreRecord),
    [discardedRecordIds, handleDiscardRecord, handleRestoreRecord],
  );
  const refreshColumns = useMemo(() => makeRefreshColumns(), []);

  // No allowlist filter on `actionType` here — unlike the previous API shape
  // (whose `changeType` needed filtering down to `{UPDATE,INSERT,DELETE,FORK}`
  // plus `REFRESH` to exclude unrelated record kinds), the refactored
  // endpoint returns exactly the audit records this modal should show.
  // Normalized once, at the API layer — see `normalizeLibraryHistoryRecord`
  // in `api/index.ts`'s own doc comment (shared with `rate-manager.tsx`'s
  // "last updated" banner, which reads the same `getLibraryHistory` data).
  const records = data ?? [];

  // TEMP diagnostic — remove once the real field names are confirmed to
  // match what this modal assumes (entityCode/entityName/entityKind/
  // actionType/fieldChanged/before/after). If Code/Name/Type/Kind are
  // rendering blank in the table, check this log for what the API is
  // actually calling those fields.
  useEffect(() => {
    if (records.length > 0) {
      // eslint-disable-next-line no-console
      console.debug('[ChangeHistoryModal] first raw record from API:', records[0]);
    }
  }, [records]);

  const batches = useMemo(() => {
    const map = new Map<string, {
      batchId: string;
      changedBy: string;
      latestAt: string;
      isAI: boolean;
      isRefresh: boolean;
      records: LibraryHistoryRecord[];
    }>();

    records.forEach((rec) => {
      // Records without a batchId (e.g. a Refresh/reset, which isn't a Censo
      // AI batch operation) must each get their own unique key - falling back
      // to a single shared '(no-batch)' key merged every batchId-less record
      // (all Reset to Regional Defaults events, regardless of when they
      // happened) into one block instead of listing them in chronological
      // order alongside everything else.
      const key = rec.batchId || `single-${rec.id}`;
      const existing = map.get(key);
      if (existing) {
        existing.records.push(rec);
        if (rec.changedAt > existing.latestAt) existing.latestAt = rec.changedAt;
      } else {
        map.set(key, {
          batchId: rec.batchId ?? '',
          changedBy: rec.changedBy,
          latestAt: rec.changedAt,
          isAI: !!rec.agentPrompt,
          isRefresh: isRefreshRecord(rec),
          records: [rec],
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => b.latestAt.localeCompare(a.latestAt));
  }, [records]);

  const filteredBatches = useMemo(() => {
    let result = batches;

    if (dateRange && (dateRange[0] || dateRange[1])) {
      const [start, end] = dateRange;
      result = result.filter((batch) => {
        const d = parseUtc(batch.latestAt);
        if (start && d < start.startOf('day').toDate()) return false;
        if (end && d > end.endOf('day').toDate()) return false;
        return true;
      });
    }

    if (searchText.trim()) {
      const lower = searchText.trim().toLowerCase();
      result = result.flatMap((batch) => {
        const matchingRecords = batch.records.filter((rec) => {
          // `entityName` is now on the record itself — the live `items`-derived
          // `nameMap` is only a fallback for a record that somehow arrives
          // without one (a `RegionalLibraryItem` covers current rate-library
          // items only, not bill lines/build-ups, so it can't be relied on).
          const name = (rec.entityName || nameMap.get(rec.entityCode) || '').toLowerCase();
          return (
            rec.entityCode.toLowerCase().includes(lower) ||
            name.includes(lower) ||
            batch.changedBy.toLowerCase().includes(lower)
          );
        });
        if (matchingRecords.length === 0) return [];
        return [{ ...batch, records: matchingRecords }];
      });
    }

    return result;
  }, [batches, searchText, dateRange, nameMap]);

  const firstBatchKey = filteredBatches.length > 0
    ? (filteredBatches[0].batchId || 'batch-0')
    : null;

  useEffect(() => {
    if (open && firstBatchKey) {
      setActiveKeys((prev) => prev.length === 0 ? [firstBatchKey] : prev);
    }
  }, [open, firstBatchKey]);

  const collapseItems = filteredBatches.map((batch, idx) => {
    const activeCount = batch.records.filter((r) => !discardedRecordIds.has(r.id)).length;
    return {
      key: batch.batchId || `batch-${idx}`,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {batch.batchId && (
            <Text style={{ fontFamily: 'monospace', fontSize: 11, color: '#888' }}>
              {batch.batchId.slice(0, 8)}…
            </Text>
          )}
          <span style={{ fontSize: 13, fontWeight: 600 }}>{batch.changedBy}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {parseUtc(batch.latestAt).toLocaleString('en-GB', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            })}
          </Text>
          {batch.isRefresh ? (
            <Tag color="orange" icon={<ReloadOutlined />} style={{ fontSize: 11, margin: 0 }}>
              Refresh
            </Tag>
          ) : (
            <Tag color="green" style={{ fontSize: 11, margin: 0 }}>
              {activeCount} change{activeCount !== 1 ? 's' : ''}
            </Tag>
          )}
          {batch.isAI && (
            <Tag color="purple" icon={<RobotOutlined />} style={{ fontSize: 11, margin: 0 }}>
              AI
            </Tag>
          )}
        </div>
      ),
      children: (
        <Table<LibraryHistoryRecord>
          size="small"
          rowKey="id"
          dataSource={batch.records}
          columns={batch.isRefresh ? refreshColumns : batchColumns}
          pagination={batch.records.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
          style={{ margin: '-4px 0' }}
          onRow={(record) => ({
            style: discardedRecordIds.has(record.id) ? { opacity: 0.45, background: '#fafafa' } : {},
          })}
        />
      ),
    };
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {(() => {
              const filteredRecordCount = filteredBatches.reduce((sum, b) => sum + b.records.length, 0);
              const isFiltered = filteredBatches.length !== batches.length || filteredRecordCount !== records.length;
              return isFiltered
                ? `${filteredRecordCount} of ${records.length} record${records.length !== 1 ? 's' : ''} · ${filteredBatches.length} of ${batches.length} save${batches.length !== 1 ? 's' : ''}`
                : `${records.length} record${records.length !== 1 ? 's' : ''} across ${batches.length} save${batches.length !== 1 ? 's' : ''}`;
            })()}
            {siteName ? ` · ${siteName}` : ''}
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => refetch()} loading={isFetching} disabled={!region}>
              Refresh
            </Button>
            <Button type="primary" onClick={onClose}>Close</Button>
          </div>
        </div>
      }
      title={
        <span>
          <HistoryOutlined style={{ marginRight: 8 }} />
          Change History
          {siteName && <Tag style={{ marginLeft: 8 }}>{siteName}</Tag>}
        </span>
      }
      width={900}
      styles={{ body: { padding: '12px 24px' } }}
      afterClose={() => {
        setActiveKeys([]);
        setSearchText('');
        setDateRange(null);
        setDiscardedRecordIds(new Set());
      }}
      afterOpenChange={(visible) => {
        if (visible && scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
      }}
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Input
          placeholder="Search code, name or user…"
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ flex: 1, minWidth: 180 }}
        />
        <DatePicker.RangePicker
          value={dateRange}
          onChange={(v) => setDateRange(v as [Dayjs | null, Dayjs | null] | null)}
          format="DD/MM/YYYY"
          allowEmpty={[true, true]}
          style={{ minWidth: 240 }}
          placeholder={['From date', 'To date']}
        />
      </div>

      <div ref={scrollRef} style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: '#aaa' }}>Loading history…</div>
          </div>
        ) : batches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
            No changes have been saved to this library yet.
          </div>
        ) : filteredBatches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
            No records match the current filters.
          </div>
        ) : (
          <Collapse
            activeKey={activeKeys}
            onChange={(keys) => setActiveKeys(typeof keys === 'string' ? [keys] : keys)}
            items={collapseItems}
            style={{ background: 'transparent' }}
          />
        )}
      </div>
    </Modal>
  );
};

// ─── AI Session History Modal ─────────────────────────────────────────────────

export interface CensoSessionHistoryModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  discardedCodes: Set<string>;
  /** Which chatbot's history to fetch — 'modify' (default) reads the library
   * assistant's history (rounds carry `changes`); 'addNew' reads the Activity
   * Assistant's own, separate history endpoint (rounds carry `operations`). */
  mode?: CensoAssistantMode;
}

const OPERATION_STATUS_COLOURS: Record<CensoActivityCompletedOperation['status'], string> = {
  ok: 'green',
  error: 'red',
  cancelled: 'default',
};

export const CensoSessionHistoryModal: React.FC<CensoSessionHistoryModalProps> = ({ open, onClose, sessionId, discardedCodes, mode = 'modify' }) => {
  const isAddNewMode = mode === 'addNew';
  const libraryHistoryQuery = useCensoHistory(sessionId, {
    enabled: open && !!sessionId && !isAddNewMode,
  });
  const activityHistoryQuery = useCensoActivityHistory(sessionId, {
    enabled: open && !!sessionId && isAddNewMode,
  });
  const { isLoading, isFetching, refetch } = isAddNewMode ? activityHistoryQuery : libraryHistoryQuery;

  // Normalise both shapes to one type the render below can share — modify-mode
  // rounds carry `changes`, addNew-mode rounds carry `operations`.
  const rounds: Array<{ message: string; timestamp: string; reply: string; changes?: { changes: CensoChange[] } | null; operations?: CensoActivityCompletedOperation[] }> =
    isAddNewMode ? (activityHistoryQuery.data?.rounds ?? []) : (libraryHistoryQuery.data?.rounds ?? []);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
            Session: {sessionId ? `${sessionId.slice(0, 8)}…${sessionId.slice(-4)}` : '—'}
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => refetch()} loading={isFetching} disabled={!sessionId}>
              Refresh
            </Button>
            <Button type="primary" onClick={onClose}>Close</Button>
          </div>
        </div>
      }
      title={
        <span>
          <RobotOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          {isAddNewMode ? 'Activity Assistant History' : 'AI Session History'}
          {rounds.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 10 }}>
              {rounds.length} exchange{rounds.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </span>
      }
      width={780}
      styles={{ body: { padding: '16px 24px', maxHeight: '65vh', overflowY: 'auto' } }}
    >
      {!sessionId ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>No active session.</div>
      ) : isLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#aaa' }}>Loading session history…</div>
        </div>
      ) : rounds.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
          No exchanges in this session yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {rounds.map((round, i) => (
            <div
              key={i}
              style={{
                borderBottom: i < rounds.length - 1 ? '1px solid #f0f0f0' : 'none',
                paddingBottom: i < rounds.length - 1 ? 24 : 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Tag color="geekblue" style={{ margin: 0, fontWeight: 600 }}>Round {i + 1}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {parseUtc(round.timestamp).toLocaleString('en-GB', {
                    day: '2-digit', month: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', second: '2-digit',
                  })}
                </Text>
                {isAddNewMode ? (
                  (round.operations?.length ?? 0) > 0 ? (
                    <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                      {round.operations!.length} operation{round.operations!.length !== 1 ? 's' : ''}
                    </Tag>
                  ) : (
                    <Tag color="default" style={{ margin: 0, fontSize: 11 }}>No operations</Tag>
                  )
                ) : (
                  (() => {
                    const visibleCount = (round.changes?.changes ?? []).filter((ch) => !discardedCodes.has(ch.c)).length;
                    return visibleCount > 0 ? (
                      <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                        {visibleCount} change{visibleCount !== 1 ? 's' : ''}
                      </Tag>
                    ) : (
                      <Tag color="default" style={{ margin: 0, fontSize: 11 }}>No changes</Tag>
                    );
                  })()
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, maxWidth: '80%' }}>
                  <div style={{
                    padding: '8px 14px',
                    borderRadius: '14px 14px 4px 14px',
                    backgroundColor: '#1890ff',
                    color: '#fff',
                    fontSize: 13,
                    lineHeight: 1.5,
                  }}>
                    {round.message}
                  </div>
                  <Avatar size={24} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff', flexShrink: 0 }} />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 12 }}>
                <Avatar size={24} icon={<RobotOutlined />} style={{ backgroundColor: '#0B2545', flexShrink: 0 }} />
                <div style={{
                  padding: '8px 14px',
                  borderRadius: '14px 14px 14px 4px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #e8e8e8',
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: '#1a1a1a',
                  maxWidth: '80%',
                  whiteSpace: 'pre-line',
                }}>
                  {isAddNewMode ? renderActivityReplyLines(round.reply) : round.reply}
                </div>
              </div>

              {isAddNewMode && round.operations?.length ? (
                <div style={{ marginLeft: 32, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {round.operations.map((op, opIdx) => (
                    <div key={opIdx} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                      <Tag color={OPERATION_STATUS_COLOURS[op.status]} style={{ margin: 0, textTransform: 'uppercase', fontSize: 10 }}>
                        {op.status}
                      </Tag>
                      {op.status === 'error' && op.error && (
                        <Text type="secondary">{op.error.message}{op.error.code ? ` (${op.error.code})` : ''}</Text>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}

              {!isAddNewMode && round.changes?.changes?.length ? (
                <div style={{ marginLeft: 32 }}>
                  <Table<CensoChange>
                    size="small"
                    rowKey="c"
                    dataSource={round.changes.changes.filter((ch) => !discardedCodes.has(ch.c))}
                    pagination={false}
                    columns={[
                      { title: 'Code', dataIndex: 'c', key: 'c', width: 100 },
                      { title: 'Name', dataIndex: 'n', key: 'n' },
                      {
                        title: 'Before (£)',
                        dataIndex: 'or',
                        key: 'or',
                        width: 100,
                        align: 'right',
                        render: (v: number) =>
                          v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                      },
                      {
                        title: 'After (£)',
                        dataIndex: 'nr',
                        key: 'nr',
                        width: 100,
                        align: 'right',
                        render: (v: number, record: CensoChange) => {
                          const diff = v - record.or;
                          return (
                            <span style={{ color: diff > 0 ? '#52c41a' : diff < 0 ? '#ff4d4f' : 'inherit', fontWeight: 600 }}>
                              {v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          );
                        },
                      },
                      {
                        title: 'Δ',
                        key: 'delta',
                        width: 65,
                        align: 'right',
                        render: (_: unknown, record: CensoChange) => {
                          const pct = ((record.nr - record.or) / record.or) * 100;
                          const sign = pct >= 0 ? '+' : '';
                          return (
                            <span style={{ color: pct >= 0 ? '#52c41a' : '#ff4d4f', fontSize: 12 }}>
                              {sign}{pct.toFixed(1)}%
                            </span>
                          );
                        },
                      },
                    ]}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
